import { supabase } from '../lib/supabase'
import { getImpersonateTenantRaw, getImpersonationSource } from './impersonationSession'

/** Shown in password inputs for saved staff rows (hash is never returned from API). */
export const STAFF_SAVED_PASSWORD_MASK = '••••••••'

export function isMaskedStaffPassword(value?: string | null): boolean {
    const v = String(value || '').trim()
    return v === STAFF_SAVED_PASSWORD_MASK || v === '********'
}

function readStaffAccessTokenFromStorage(): string | null {
    if (typeof window === 'undefined') return null
    const keys = ['staff_session', 'staff_session_shared']
    for (const key of keys) {
        try {
            const raw = sessionStorage.getItem(key) || localStorage.getItem(key)
            if (!raw) continue
            const session = JSON.parse(raw) as { access_token?: string }
            const token = String(session.access_token || '').trim()
            if (token.split('.').length === 3) return token
        } catch {
            continue
        }
    }
    return null
}

export type StaffListEntry = {
    username: string
    password?: string
    email?: string
    allowAddClient: boolean
    allowSystemSettings: boolean
    allowBranding: boolean
    allowViewLogs: boolean
    allowDashboard: boolean
    allowImpersonate: boolean
}

export const MASTER_STAFF_USERNAMES = ['su', 'devsu'] as const

export const STAFF_CONFIG_UPDATED_KEY = 'staff_config_updated_at'

const STAFF_SESSION_SYNC_KEYS = [
    'username',
    'email',
    'role',
    'allowAddClient',
    'allowSystemSettings',
    'allowBranding',
    'allowViewLogs',
    'allowDashboard',
    'allowImpersonate',
] as const

export function isMasterStaffUsername(username?: string | null): boolean {
    const key = (username || '').trim().toLowerCase()
    return (MASTER_STAFF_USERNAMES as readonly string[]).includes(key)
}

export function isSettingsStaffUsername(username?: string | null): boolean {
    return isMasterStaffUsername(username)
}

export function canStaffAccessSystemSettings(session: StaffSessionLike | null | undefined): boolean {
    if (!session) return true
    return isSettingsStaffUsername(session.username)
}

export function isPlatformSuperAdminProfile(role?: string | null): boolean {
    return ['super_admin', 'superadmin'].includes((role || '').trim().toLowerCase())
}

export type StaffSessionLike = {
    username?: string | null
    allowAddClient?: boolean
    allowSystemSettings?: boolean
    allowBranding?: boolean
    allowViewLogs?: boolean
    allowDashboard?: boolean
    allowImpersonate?: boolean
}

/** Staff with dashboard access only — no add client, settings, logs, branding, or impersonate. */
export function isStaffDashboardViewOnly(session: StaffSessionLike | null | undefined): boolean {
    if (!session) return false
    if (isMasterStaffUsername(session.username)) return false
    if (session.allowDashboard === false) return false
    return (
        !session.allowAddClient &&
        !session.allowBranding &&
        !session.allowViewLogs &&
        !session.allowImpersonate
    )
}

/** Whether the admin dashboard accounts table should show the Actions column. */
export function staffCanShowDashboardActions(session: StaffSessionLike | null | undefined): boolean {
    if (!session) return true
    if (isMasterStaffUsername(session.username)) return true
    return Boolean(session.allowImpersonate)
}

export function staffCanImpersonate(session: StaffSessionLike | null | undefined): boolean {
    if (!session) return true
    if (isMasterStaffUsername(session.username)) return true
    return Boolean(session.allowImpersonate)
}

/** Active admin-dashboard impersonation (not control-account native sessions). */
export function isStaffAdminImpersonationActive(): boolean {
    if (!getImpersonateTenantRaw()) return false
    return getImpersonationSource() === 'admin'
}

export function shouldClearStaffImpersonation(session: StaffSessionLike | null | undefined): boolean {
    if (!session || staffCanImpersonate(session)) return false
    return isStaffAdminImpersonationActive()
}

export const STAFF_ADMIN_FALLBACK_ROUTE = '/admin/dashboard'

const DASHBOARD_SETTINGS_TABS = new Set([
    'staff',
    'email',
    'business-entries',
    'integrations',
    'client-navigation',
    'branding',
])

function normalizeAdminPath(pathname: string): string {
    const base = pathname.split('?')[0]
    if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1)
    return base
}

function isAdminClientDetailPath(path: string): boolean {
    if (path === '/admin/clients') return true
    if (path === '/admin/clients/new') return false
    if (/^\/admin\/clients\/[^/]+$/.test(path)) return true
    if (/^\/admin\/clients\/[^/]+\/(mapping|locations)$/.test(path)) return true
    if (/^\/admin\/locations\//.test(path)) return true
    return false
}

function isAdminCreateOrEditClientPath(path: string): boolean {
    return (
        path === '/admin/clients/new' ||
        path === '/admin/add-new-client' ||
        path === '/admin/add-master-club' ||
        /^\/admin\/edit-client\//.test(path)
    )
}

function isDevSuStaffUsername(username?: string | null): boolean {
    return (username || '').trim().toLowerCase() === 'devsu'
}

/** Master staff except devsu — su bypasses non-client admin routes only. */
function isMasterStaffExceptDevSu(username?: string | null): boolean {
    return isMasterStaffUsername(username) && !isDevSuStaffUsername(username)
}

/** Whether the current staff session may stay on this admin route. Non-staff sessions are unrestricted. */
export function canStaffAccessAdminPath(
    pathname: string,
    session: StaffSessionLike | null | undefined
): boolean {
    if (!session) return true

    const path = normalizeAdminPath(pathname)
    if (!path.startsWith('/admin')) return true

    if (isDevSuStaffUsername(session.username)) return true

    if (path === '/admin' || path === STAFF_ADMIN_FALLBACK_ROUTE) {
        return session.allowDashboard !== false
    }

    // Client management — enforce flags for all staff except devsu (including su).
    if (isAdminCreateOrEditClientPath(path)) {
        return !!session.allowAddClient
    }

    if (isAdminClientDetailPath(path)) {
        return !!session.allowAddClient
    }

    if (path.startsWith('/admin/settings') || path === '/admin/email-monitor') {
        return canStaffAccessSystemSettings(session)
    }

    if (path === '/admin/supabase-logs' || path === '/admin/aws-logs') {
        return isDevSuStaffUsername(session.username)
    }

    if (isMasterStaffExceptDevSu(session.username)) return true

    if (isStaffDashboardViewOnly(session)) return false

    if (path === '/admin/user-logs') {
        return !!session.allowViewLogs
    }

    if (path === '/admin/branding' || path === '/admin/survey-branding') {
        return !!session.allowBranding || session.username?.toLowerCase() === 'devsu'
    }

    return !!(
        session.allowAddClient ||
        session.allowViewLogs ||
        session.allowBranding
    )
}

/** Dashboard sub-tabs that require Settings (or branding for the branding tab). */
export function canStaffAccessDashboardTab(
    tab: string,
    session: StaffSessionLike | null | undefined
): boolean {
    if (!DASHBOARD_SETTINGS_TABS.has(tab)) return true
    if (!session) return true
    if (canStaffAccessSystemSettings(session)) return true
    if (tab === 'branding') {
        return !!session.allowBranding || session.username?.toLowerCase() === 'devsu'
    }
    return false
}

/** Returns fallback route when staff may not access the path; null when allowed. */
export function getStaffAdminRedirectPath(
    pathname: string,
    session: StaffSessionLike | null | undefined
): string | null {
    if (canStaffAccessAdminPath(pathname, session)) return null
    return STAFF_ADMIN_FALLBACK_ROUTE
}

/**
 * Unified staff permission redirect — admin routes plus impersonation revocation
 * (tenant/control views and active admin impersonation sessions).
 */
export function getStaffPermissionRedirectPath(
    pathname: string,
    session: StaffSessionLike | null | undefined
): string | null {
    if (!session) return null

    const path = normalizeAdminPath(pathname)
    const fallback = STAFF_ADMIN_FALLBACK_ROUTE

    if (!staffCanImpersonate(session)) {
        if (path.startsWith('/tenant') || path.startsWith('/control')) {
            return path === fallback ? null : fallback
        }

        if (isStaffAdminImpersonationActive() && path !== fallback) {
            return fallback
        }
    }

    if (path.startsWith('/admin')) {
        return getStaffAdminRedirectPath(pathname, session)
    }

    return null
}

export function normalizeStaffEntry(entry: any): StaffListEntry {
    return {
        username: String(entry?.username || '').trim().toLowerCase(),
        password: String(entry?.password || '').trim(),
        email: entry?.email ? String(entry.email).trim().toLowerCase() : undefined,
        allowAddClient: !!entry?.allowAddClient,
        allowSystemSettings: !!entry?.allowSystemSettings,
        allowBranding: !!entry?.allowBranding,
        allowViewLogs: !!entry?.allowViewLogs,
        allowDashboard: entry?.allowDashboard !== false,
        allowImpersonate: !!entry?.allowImpersonate,
    }
}

export function parseStaffEntries(usernamesRaw: unknown): StaffListEntry[] {
    if (Array.isArray(usernamesRaw)) {
        return usernamesRaw.map(normalizeStaffEntry)
    }

    try {
        const parsed = typeof usernamesRaw === 'string' ? JSON.parse(usernamesRaw) : usernamesRaw
        return Array.isArray(parsed) ? parsed.map(normalizeStaffEntry) : []
    } catch (error) {
        console.error('[staffPermissionSync] Failed to parse staff entries', error)
        return []
    }
}

/** Normalize edge-function / REST shapes into system_settings rows. */
export function extractSystemSettingsRows(result: unknown): Array<{ key: string; value: unknown }> {
    if (!result) return []
    if (Array.isArray(result)) {
        return result as Array<{ key: string; value: unknown }>
    }
    if (typeof result === 'object') {
        const obj = result as Record<string, unknown>
        if (Array.isArray(obj.data)) {
            return obj.data as Array<{ key: string; value: unknown }>
        }
        if (obj.data && typeof obj.data === 'object' && Array.isArray((obj.data as { data?: unknown }).data)) {
            return (obj.data as { data: Array<{ key: string; value: unknown }> }).data
        }
    }
    return []
}

export function findStaffEntry(allowed: StaffListEntry[], login: string): StaffListEntry | undefined {
    const raw = (login || '').trim().toLowerCase()
    if (!raw) return undefined

    for (const entry of allowed) {
        if (entry.username === raw) return entry
        if (entry.email && entry.email === raw) return entry
    }
    return undefined
}

export function staffSessionPermissionSignature(session: Record<string, any> | null): string {
    if (!session) return ''
    const payload: Record<string, unknown> = {}
    for (const key of STAFF_SESSION_SYNC_KEYS) {
        if (key.startsWith('allow')) {
            payload[key] = Boolean(session[key])
        } else {
            payload[key] = String(session[key] ?? '').trim().toLowerCase()
        }
    }
    return JSON.stringify(payload)
}

export async function fetchStaffListFromServer(): Promise<StaffListEntry[]> {
    const staffToken = readStaffAccessTokenFromStorage()
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '')
    const anonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim()

    const select =
        'username,allow_add_client,allow_system_settings,allow_branding,allow_view_logs,allow_dashboard,allow_impersonate'
    const restUrl = `${supabaseUrl}/rest/v1/staff_users?select=${select}&order=created_at.asc`

    const headers: Record<string, string> = {
        apikey: anonKey,
        Authorization: `Bearer ${staffToken || anonKey}`,
        Accept: 'application/json',
    }

    const response = await fetch(restUrl, { headers })
    if (!response.ok) {
        const { data, error } = await supabase
            .from('staff_users')
            .select('username, allow_add_client, allow_system_settings, allow_branding, allow_view_logs, allow_dashboard, allow_impersonate')
            .order('created_at', { ascending: true })
        if (error) throw error
        return (data || []).map((row: any): StaffListEntry => ({
            username: String(row.username || '').trim().toLowerCase(),
            email: row.email,
            allowAddClient: Boolean(row.allow_add_client),
            allowSystemSettings: Boolean(row.allow_system_settings),
            allowBranding: Boolean(row.allow_branding),
            allowViewLogs: Boolean(row.allow_view_logs),
            allowDashboard: Boolean(row.allow_dashboard),
            allowImpersonate: Boolean(row.allow_impersonate),
        }))
    }

    const data = (await response.json()) as any[]

    return (data || []).map((row: any): StaffListEntry => ({
        username: String(row.username || '').trim().toLowerCase(),
        email: row.email,
        allowAddClient: Boolean(row.allow_add_client),
        allowSystemSettings: Boolean(row.allow_system_settings),
        allowBranding: Boolean(row.allow_branding),
        allowViewLogs: Boolean(row.allow_view_logs),
        allowDashboard: Boolean(row.allow_dashboard),
        allowImpersonate: Boolean(row.allow_impersonate),
    }))
}

export function buildStaffSessionFromEntry(
    currentSession: Record<string, any>,
    staffUser: StaffListEntry
): Record<string, any> {
    return {
        ...currentSession,
        username: String(currentSession.username || staffUser.username).trim().toLowerCase(),
        email: currentSession.email || staffUser.email,
        role: currentSession.role || 'SuperAdmin',
        allowAddClient: Boolean(staffUser.allowAddClient),
        allowSystemSettings: Boolean(staffUser.allowSystemSettings),
        allowBranding: Boolean(staffUser.allowBranding),
        allowViewLogs: Boolean(staffUser.allowViewLogs),
        allowDashboard: Boolean(staffUser.allowDashboard),
        allowImpersonate: Boolean(staffUser.allowImpersonate),
    }
}

export function notifyStaffConfigUpdated() {
    if (typeof window === 'undefined') return
    try {
        localStorage.setItem(STAFF_CONFIG_UPDATED_KEY, String(Date.now()))
    } catch {
        /* ignore */
    }
    window.dispatchEvent(new CustomEvent('staff-config-updated'))
}
